'use client';

import ReactECharts from 'echarts-for-react';
import { useTheme } from 'next-themes';
import { useEffect, useState, useMemo } from 'react';
import { ParsedFitResult } from '@/lib/fitParser';
import type { WellnessRecord, ActivityMetric } from '@/lib/pbSync';
import { calculatePMC, calculateBikeTSS, DailyTSS } from '@/lib/pmc';

// ─── Palette (blue/teal/green dominant) ──────────────────────────────────────
const C = {
  blue:    '#3b82f6',  // primary series
  teal:    '#14b8a6',  // secondary series
  cyan:    '#06b6d4',  // body battery / accents
  sky:     '#0ea5e9',  // highlights
  emerald: '#10b981',  // good/positive
  rose:    '#f43f5e',  // danger only
  amber:   '#f59e0b',  // caution only
  violet:  '#8b5cf6',  // imbalances
  slate:   '#94a3b8',  // muted/bars
};

function cb(isDark: boolean) {
  return {
    text: isDark ? '#94a3b8' : '#6b7280',
    grid: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    tt:   { backgroundColor: isDark ? '#1c1c1e' : '#fff', borderColor: 'transparent', textStyle: { color: isDark ? '#f0f0f0' : '#1a1a1a', fontSize: 11 } },
    bg:   isDark ? '#0a0a0a' : '#f5f5f7',
  };
}

/**
 * Picks an ECharts xAxis interval and label formatter appropriate for the
 * number of days in the series — always renders 5-8 readable ticks.
 */
function xAxisConfig(dates: string[], textColour: string, gridColour: string) {
  const n = dates.length;
  // interval = how many data-points to skip between labels
  const interval =
    n <= 14  ? 1  :   // every day
    n <= 35  ? 6  :   // every week
    n <= 90  ? 13 :   // every 2 weeks
    n <= 180 ? 29 :   // every month
    n <= 365 ? 59 :   // every 2 months
               89;    // every 3 months

  // Formatter: short date that fits the density
  const formatter = (val: string) => {
    const d = new Date(val);
    if (n <= 35) {
      // "9 Jul"
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    }
    if (n <= 180) {
      // "9 Jul"
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    }
    // "Jul '25"
    return d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
  };

  return {
    type: 'category' as const,
    boundaryGap: false,
    axisLine: { lineStyle: { color: gridColour } },
    axisTick: { show: false },
    axisLabel: { color: textColour, fontSize: 10, interval, formatter },
  };
}

// ─── Shared primitives ────────────────────────────────────────────────────────
function StatCard({ label, value, sub, accent }: { label: string; value: string | number; sub?: string; accent?: string }) {
  return (
    <div className="flex flex-col gap-0.5 sm:gap-1 p-3 sm:p-5 rounded-2xl bg-card border border-border/60">
      <span className="text-[10px] sm:text-xs font-medium text-muted-foreground tracking-wide uppercase">{label}</span>
      <span className="text-xl sm:text-3xl font-semibold tracking-tight" style={{ color: accent }}>{value}</span>
      {sub && <span className="text-[10px] sm:text-xs text-muted-foreground">{sub}</span>}
    </div>
  );
}

function Section({ title, desc, children }: { title: string; desc?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold tracking-tight">{title}</h2>
        {desc && <p className="text-sm text-muted-foreground mt-0.5">{desc}</p>}
      </div>
      {children}
    </div>
  );
}

function ChartCard({ title, desc, children, span = '' }: { title: string; desc?: string; children: React.ReactNode; span?: string }) {
  return (
    <div className={`flex flex-col rounded-2xl bg-card border border-border/50 shadow-[0_2px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.28)] overflow-hidden ${span}`}>
      <div className="px-5 pt-5 pb-4 border-b border-border/40">
        <p className="text-sm font-semibold tracking-tight">{title}</p>
        {desc && <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{desc}</p>}
      </div>
      <div className="p-5 flex-1">{children}</div>
    </div>
  );
}

function InsightCard({ colour, label, body }: { colour: string; label: string; body: string }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-2xl bg-card border border-border/50 shadow-[0_2px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.28)]">
      <div className="w-1 self-stretch rounded-full flex-shrink-0" style={{ background: colour }} />
      <div>
        <p className="text-sm font-semibold">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

// ─── Constants ────────────────────────────────────────────────────────────────
const BIKE_FTP  = 184;   // watts
const RUN_LTHR  = 181;   // bpm  (used for both run and swim HR zones)
const RUN_ZONE_PCT = [0.75, 0.82, 0.89, 0.94, 1.00]; // % LTHR thresholds Z1-Z5

// ─── Component ────────────────────────────────────────────────────────────────
interface Props {
  realData?:     ParsedFitResult[]  | null;
  wellnessData?: WellnessRecord[];
  metricsData?:  ActivityMetric[];
}

export function AdvancedSportScience({ realData, wellnessData = [], metricsData = [] }: Props) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const isDark = mounted ? resolvedTheme === 'dark' : true;
  const { text, grid, tt, bg } = cb(isDark);

  // ─── Training-load pipeline ──────────────────────────────────────────────
  const computed = useMemo(() => {
    const today = new Date();

    let spanStart = new Date(today);
    spanStart.setDate(today.getDate() - 41);
    if (realData?.length) {
      spanStart = new Date(realData.reduce((m, w) => w.startTime < m ? w.startTime : m, realData[0].startTime));
    }

    const dates: string[] = [];
    for (const d = new Date(spanStart); d <= today; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().split('T')[0]);
    }

    const tssMap = new Map<string, number>();
    let z12 = 0, z3 = 0, z45 = 0;
    const efByDate: { date: string; ef: number; sport: string }[] = [];
    const vo2ByDate: { date: string; vo2: number }[] = [];

    if (realData?.length) {
      realData.forEach(w => {
        const key  = w.startTime.toISOString().split('T')[0];
        const sp   = (w.sport ?? '').toLowerCase();
        const isBike = sp.includes('cycl') || sp.includes('bik');
        const isRun  = sp.includes('run');

        // TSS
        let tss = 0;
        if (w.normalizedPower && isBike) {
          tss = calculateBikeTSS(w.totalDuration, w.normalizedPower, BIKE_FTP);
        } else if (w.avgHeartRate > 100) {
          tss = (w.totalDuration / 3600) * ((w.avgHeartRate - 100) / 100) * 100;
        } else {
          tss = (w.totalDuration / 3600) * 50;
        }
        tssMap.set(key, (tssMap.get(key) || 0) + tss);

        // Polarisation — sport-aware
        if (isBike && w.normalizedPower) {
          if (w.normalizedPower < BIKE_FTP * 0.8) z12++;
          else if (w.normalizedPower < BIKE_FTP * 0.95) z3++;
          else z45++;
        } else if (w.avgHeartRate > 0) {
          if (w.avgHeartRate < RUN_LTHR * 0.82) z12++;
          else if (w.avgHeartRate < RUN_LTHR * 0.94) z3++;
          else z45++;
        }

        // Efficiency Factor
        if (w.normalizedPower && w.avgHeartRate > 0) {
          efByDate.push({ date: key, ef: +(w.normalizedPower / w.avgHeartRate).toFixed(3), sport: sp });
        }
      });
    } else {
      // demo seed
      dates.forEach((d, i) => tssMap.set(d, Math.abs(Math.sin(i * 0.4)) * 120));
      z12 = 74; z3 = 16; z45 = 10;
    }

    // Merge VO2max from metricsData
    metricsData.forEach(m => {
      if (m.vo2max) vo2ByDate.push({ date: m.date, vo2: m.vo2max });
    });

    const pmcInput: DailyTSS[] = dates.map(d => ({ date: d, tss: tssMap.get(d) || 0 }));
    const pmc = calculatePMC(pmcInput, 38, 28);

    const acwr = pmc.map(p => +((p.atl) / (p.ctl > 0 ? p.ctl : 1)).toFixed(2));

    // Monotony & Strain
    const monotony: number[] = [];
    const strain:   number[] = [];
    for (let i = 0; i < pmc.length; i++) {
      if (i < 6) { monotony.push(1); strain.push(pmc[i].tss); continue; }
      const w7   = pmc.slice(i - 6, i + 1).map(x => x.tss);
      const mean = w7.reduce((a, b) => a + b, 0) / 7;
      const std  = Math.sqrt(w7.reduce((a, b) => a + (b - mean) ** 2, 0) / 7) || 1;
      const m    = mean / std;
      monotony.push(+m.toFixed(2));
      strain.push(Math.round(pmc[i].tss * m));
    }

    // Ramp rate
    const rampDates: string[] = [];
    const rampVals:  number[] = [];
    for (let i = 7; i < pmc.length; i += 7) {
      rampDates.push(pmc[i].date);
      rampVals.push(+(pmc[i].ctl - pmc[i - 7].ctl).toFixed(1));
    }

    const last        = pmc[pmc.length - 1];
    const latestACWR  = acwr[acwr.length - 1] ?? 1;
    const readiness   = Math.max(0, Math.min(100,
      50 + (last?.tsb ?? 0) * 1.2 - (latestACWR > 1.3 ? (latestACWR - 1.3) * 60 : 0)
    ));
    const xInterval   = Math.max(1, Math.floor(dates.length / 7));

    return {
      dates, pmc, acwr, monotony, strain, rampDates, rampVals, efByDate, vo2ByDate,
      z12, z3, z45, latestACWR, latestTSB: last?.tsb ?? 0,
      latestCTL: last?.ctl ?? 0, latestATL: last?.atl ?? 0,
      readiness: Math.round(readiness), xInterval,
    };
  }, [realData, metricsData]);

  // ─── Insights ────────────────────────────────────────────────────────────
  const insights = useMemo(() => {
    const out: { colour: string; label: string; body: string }[] = [];
    const { latestACWR, latestTSB, z12, z3, z45 } = computed;
    const total = z12 + z3 + z45;

    if (latestACWR > 1.5)       out.push({ colour: C.rose,    label: 'High load warning',  body: `Acute load is ${latestACWR}× your chronic baseline. Reduce volume before your next hard session.` });
    else if (latestACWR < 0.8)  out.push({ colour: C.amber,   label: 'Training dip',       body: 'Load has dropped below your chronic baseline. A structured build week will arrest detraining.' });
    else                         out.push({ colour: C.emerald, label: 'Load in range',      body: `ACWR is ${latestACWR} — you're building fitness without excessive injury risk.` });

    if (latestTSB < -20)         out.push({ colour: C.rose,   label: 'Deep fatigue',       body: `Form is ${latestTSB}. Prioritise sleep and an easy session or rest day before racing.` });
    else if (latestTSB > 10)     out.push({ colour: C.sky,    label: 'Peak readiness',     body: 'Form is elevated — ideal timing for a race, time trial, or benchmark effort.' });
    else                          out.push({ colour: C.blue,   label: 'Building phase',    body: `Form is ${latestTSB}. Still in accumulation — stay the course.` });

    if (total > 0) {
      const litPct = Math.round((z12 / total) * 100);
      if (litPct >= 75) out.push({ colour: C.emerald, label: 'Good polarisation',  body: `${litPct}% of sessions are low intensity — in line with the 80/20 principle.` });
      else               out.push({ colour: C.violet,  label: 'Intensity imbalance', body: `Only ${litPct}% low intensity. Move moderate sessions to genuine Zone 2 or full hard efforts.` });
    }

    // Sleep insight from wellness
    if (wellnessData.length > 0) {
      const recent = wellnessData.slice(-7);
      const avgSleep = recent.reduce((a, w) =>
        a + ((w.sleep_deep ?? 0) + (w.sleep_rem ?? 0) + (w.sleep_light ?? 0)), 0) / (recent.length || 1);
      if (avgSleep > 0) {
        if (avgSleep < 300) out.push({ colour: C.rose,    label: 'Poor sleep volume', body: `Averaging ${Math.round(avgSleep)} min/night this week. Target 420–480 min for optimal recovery.` });
        else                 out.push({ colour: C.emerald, label: 'Solid sleep',       body: `Averaging ${Math.round(avgSleep)} min/night this week. Keep protecting your sleep window.` });
      }
    }

    return out.slice(0, 4);
  }, [computed, wellnessData]);

  if (!mounted) return null;

  // ─── Chart configs ────────────────────────────────────────────────────────

  const axisX = (data: string[]) => ({ ...xAxisConfig(data, text, grid), data });
  const axisY = (name = '') => ({
    type: 'value' as const, name,
    axisLine: { show: false }, axisTick: { show: false },
    axisLabel: { color: text, fontSize: 10 },
    splitLine: { lineStyle: { color: grid, type: 'dashed' as const } },
  });
  const baseOpt = { backgroundColor: 'transparent', textStyle: { color: text, fontFamily: 'Inter, sans-serif', fontSize: 11 } };
  const gCommon = { left: 0, right: 0, bottom: 24, top: 8, containLabel: true };

  // 1. PMC
  const pmcOpt = {
    ...baseOpt, tooltip: { trigger: 'axis', ...tt },
    legend: { data: ['Fitness', 'Fatigue', 'Form', 'Daily load'], textStyle: { color: text, fontSize: 11 }, bottom: 0, itemWidth: 12, itemHeight: 12 },
    grid: { ...gCommon, bottom: 32 },
    xAxis: axisX(computed.pmc.map(d => d.date)),
    yAxis: [axisY(), { type: 'value' as const, splitLine: { show: false }, axisLabel: { show: false }, axisLine: { show: false }, axisTick: { show: false } }],
    series: [
      { name: 'Fitness',    type: 'line', data: computed.pmc.map(d => d.ctl), showSymbol: false, smooth: 0.3, lineStyle: { color: C.blue,  width: 2.5 }, itemStyle: { color: C.blue  } },
      { name: 'Fatigue',    type: 'line', data: computed.pmc.map(d => d.atl), showSymbol: false, smooth: 0.3, lineStyle: { color: C.rose,  width: 2 }, itemStyle: { color: C.rose  } },
      { name: 'Form',       type: 'line', data: computed.pmc.map(d => d.tsb), showSymbol: false, smooth: 0.3, lineStyle: { color: C.teal,  width: 2, type: 'dashed' as const }, itemStyle: { color: C.teal },
        markArea: {
          data: [
            [{ yAxis: -30, itemStyle: { color: `${C.teal}15` } }, { yAxis: -10 }],
            [{ yAxis: 15, itemStyle: { color: `${C.emerald}15` } }, { yAxis: 25 }]
          ]
        }
      },
      { name: 'Daily load', type: 'bar',  data: computed.pmc.map(d => d.tss), yAxisIndex: 1, itemStyle: { color: `${C.slate}25`, borderRadius: [2, 2, 0, 0] }, barMaxWidth: 8 },
    ],
  };

  // 2. ACWR — dynamic y max so values are never clipped
  const acwrMax = Math.max(2.0, ...(computed.acwr.filter(Boolean))) + 0.2;
  const acwrOpt = {
    ...baseOpt, tooltip: { trigger: 'axis', ...tt },
    grid: { ...gCommon, top: 24 },
    xAxis: axisX(computed.dates),
    yAxis: { ...axisY('ACWR'), min: 0, max: +acwrMax.toFixed(1) },
    visualMap: { show: false, pieces: [
      { gt: 0, lte: 0.8, color: C.amber },
      { gt: 0.8, lte: 1.3, color: C.emerald },
      { gt: 1.3, lte: 1.5, color: C.amber },
      { gt: 1.5, color: C.rose },
    ]},
    series: [{
      name: 'ACWR', type: 'line', data: computed.acwr, showSymbol: false, smooth: 0.4, lineStyle: { width: 2 },
      markArea: {
        data: [
          [{ yAxis: 0.8, itemStyle: { color: `${C.emerald}15` } }, { yAxis: 1.3 }]
        ]
      },
      markLine: { silent: true, symbol: 'none', lineStyle: { color: C.rose, type: 'dashed' as const, width: 1 },
        data: [{ yAxis: 1.5, label: { formatter: 'Danger', color: C.rose, fontSize: 10 } }] },
    }],
  };

  // 3. Monotony & Strain
  const strainOpt = {
    ...baseOpt, tooltip: { trigger: 'axis', ...tt },
    legend: { data: ['Strain', 'Monotony'], textStyle: { color: text, fontSize: 11 }, bottom: 0, itemWidth: 12, itemHeight: 12 },
    grid: { ...gCommon, bottom: 32 },
    xAxis: axisX(computed.dates),
    yAxis: [axisY('Strain'), { type: 'value' as const, splitLine: { show: false }, axisLabel: { show: false }, axisLine: { show: false }, axisTick: { show: false } }],
    series: [
      { name: 'Strain',   type: 'bar',  data: computed.strain,   itemStyle: { color: `${C.teal}cc`, borderRadius: [2, 2, 0, 0] }, barMaxWidth: 10 },
      { name: 'Monotony', type: 'line', data: computed.monotony, yAxisIndex: 1, showSymbol: false, smooth: 0.4, lineStyle: { color: C.rose, width: 2 }, itemStyle: { color: C.rose },
        markArea: {
          data: [
            [{ yAxis: 2.0, itemStyle: { color: `${C.rose}15` } }, { yAxis: 'max' }]
          ]
        }
      },
    ],
  };

  // 4. Polarisation
  const polOpt = {
    ...baseOpt,
    tooltip: { trigger: 'item', formatter: '{b}: {d}%', ...tt },
    series: [{ type: 'pie', radius: ['55%', '78%'], center: ['50%', '50%'],
      itemStyle: { borderRadius: 4, borderColor: bg, borderWidth: 3 }, label: { show: false },
      data: [
        { value: computed.z12, name: 'Zone 1–2', itemStyle: { color: C.emerald } },
        { value: computed.z3,  name: 'Zone 3',   itemStyle: { color: C.amber   } },
        { value: computed.z45, name: 'Zone 4–5', itemStyle: { color: C.rose    } },
      ],
    }],
  };

  // 5. Ramp rate
  const rampOpt = {
    ...baseOpt, tooltip: { trigger: 'axis', formatter: '{b}: {c} pts/wk', ...tt },
    grid: gCommon,
    xAxis: { ...axisX(computed.rampDates), boundaryGap: true },
    yAxis: axisY('CTL Δ'),
    series: [{ type: 'bar', data: computed.rampVals,
      itemStyle: { color: (p: any) => p.value > 8 ? C.rose : p.value < 0 ? C.sky : C.emerald, borderRadius: [3, 3, 0, 0] },
      barMaxWidth: 24,
    }],
  };

  // 6. Sleep architecture (from wellness)
  const hasWellness = wellnessData.length > 0;
  const sleepDates  = wellnessData.map(w => w.date);
  const sleepOpt = {
    ...baseOpt, tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' as const }, ...tt },
    legend: { data: ['Deep', 'REM', 'Light'], textStyle: { color: text, fontSize: 11 }, bottom: 0, itemWidth: 12, itemHeight: 12 },
    grid: { ...gCommon, bottom: 32 },
    xAxis: axisX(sleepDates),
    yAxis: [axisY('min'), { ...axisY('Score'), min: 0, max: 100, splitLine: { show: false }, axisLabel: { show: false } }],
    series: [
      { name: 'Deep',  type: 'bar', stack: 'sleep', data: wellnessData.map(w => w.sleep_deep  ?? 0), itemStyle: { color: '#1e3a8a' } },
      { name: 'REM',   type: 'bar', stack: 'sleep', data: wellnessData.map(w => w.sleep_rem   ?? 0), itemStyle: { color: C.blue } },
      { name: 'Light', type: 'bar', stack: 'sleep', data: wellnessData.map(w => w.sleep_light ?? 0), itemStyle: { color: `${C.sky}99`, borderRadius: [2, 2, 0, 0] } },
      { name: 'Sleep score', type: 'line', yAxisIndex: 1, data: wellnessData.map(w => w.sleep_score ?? null),
        showSymbol: false, smooth: 0.4, lineStyle: { color: C.teal, width: 2, type: 'dashed' as const }, itemStyle: { color: C.teal } },
    ],
  };

  // 7. HRV & Body Battery
  const hrvOpt = {
    ...baseOpt, tooltip: { trigger: 'axis', ...tt },
    legend: { data: ['HRV (ms)', 'Body Battery'], textStyle: { color: text, fontSize: 11 }, bottom: 0, itemWidth: 12, itemHeight: 12 },
    grid: { ...gCommon, bottom: 32 },
    xAxis: axisX(sleepDates),
    yAxis: [
      { ...axisY('ms'), min: (v: any) => Math.max(0, v.min - 10) },
      { type: 'value' as const, min: 0, max: 100, splitLine: { show: false }, axisLabel: { show: false }, axisLine: { show: false }, axisTick: { show: false } },
    ],
    series: [
      { name: 'HRV (ms)', type: 'line', data: wellnessData.map(w => w.hrv ?? null), showSymbol: false, smooth: 0.5,
        lineStyle: { color: C.emerald, width: 2 }, itemStyle: { color: C.emerald }, areaStyle: { color: `${C.emerald}18` } },
      { name: 'Body Battery', type: 'line', yAxisIndex: 1, data: wellnessData.map(w => w.body_battery ?? null), showSymbol: false, smooth: 0.4,
        lineStyle: { color: C.cyan, width: 2, type: 'dashed' as const }, itemStyle: { color: C.cyan } },
    ],
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-10">

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Fitness"   value={Math.round(computed.latestCTL)} sub="CTL · 42-day load"        accent={C.blue} />
        <StatCard label="Fatigue"   value={Math.round(computed.latestATL)} sub="ATL · 7-day load"         accent={C.rose} />
        <StatCard label="Form"      value={`${computed.latestTSB > 0 ? '+' : ''}${Math.round(computed.latestTSB)}`} sub="TSB · Fitness − Fatigue" accent={computed.latestTSB >= 0 ? C.teal : C.amber} />
        <StatCard label="Readiness" value={`${computed.readiness}%`}       sub="Form, ACWR & sleep"       accent={computed.readiness >= 70 ? C.emerald : computed.readiness >= 45 ? C.amber : C.rose} />
      </div>

      {/* Insights */}
      <Section title="Today's read" desc="Personalised cues from your training and sleep data.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {insights.map((ins, i) => <InsightCard key={i} {...ins} />)}
        </div>
      </Section>

      {/* PMC */}
      <Section title="Performance management" desc="Fitness, fatigue and form — the three pillars of peak performance.">
        <ChartCard title="PMC — Fitness · Fatigue · Form · Daily load" desc="Teal band: Optimal training (-10 to -30). Green band: Race ready (+15 to +25).">
          <ReactECharts option={pmcOpt} style={{ height: 280 }} />
        </ChartCard>
      </Section>

      {/* Load management */}
      <Section title="Load management" desc="Are you building too fast, too slow, or just right?">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Acute:Chronic Workload Ratio" desc="Safe build phase: 0.8–1.3 (Green band). Injury risk spikes sharply above 1.5.">
            <ReactECharts option={acwrOpt} style={{ height: 200 }} />
          </ChartCard>
          <ChartCard title="Monotony & Strain" desc="High monotony implies a lack of intensity polarisation. Red band (>2.0) = overtraining risk.">
            <ReactECharts option={strainOpt} style={{ height: 200 }} />
          </ChartCard>
        </div>
      </Section>

      {/* Recovery — sleep */}
      <Section title="Recovery" desc="Sleep is where adaptation happens. These charts use your synced wellness records.">
        {hasWellness ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title="Sleep architecture" desc="Deep sleep drives GH release and muscle repair. REM consolidates motor memory.">
              <ReactECharts option={sleepOpt} style={{ height: 260 }} />
            </ChartCard>
            <ChartCard title="HRV & body battery" desc="Rising HRV and high body battery = green light to train hard.">
              <ReactECharts option={hrvOpt} style={{ height: 260 }} />
            </ChartCard>
          </div>
        ) : (
          <div className="rounded-2xl bg-card border border-border/60 border-dashed p-8 text-center">
            <p className="text-sm text-muted-foreground">No wellness records yet — sync from PocketBase to load sleep and HRV data.</p>
          </div>
        )}
      </Section>



    </div>
  );
}
